import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import Slideshow from '../components/Slideshow';

// Create a separate axios instance for public endpoints
const publicApi = axios.create({
  baseURL: process.env.NODE_ENV === 'production' 
    ? '' // In production, use relative path
    : 'http://localhost:2001', // In development, use the full server URL
});

interface Image {
  id: string;
  url: string;
  filename: string;
  createdAt: string;
}

const GalleryContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg,rgb(255, 199, 44) 20%,rgb(255, 146, 44) 100%);
  position: relative;
  overflow: hidden;
  padding: 20px 20px;
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  z-index: 2;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
  padding: 30px;
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  animation: slideUp 0.5s ease-out;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(to right, var(--primary-light), var(--secondary-light));
    border-radius: 0 0 var(--border-radius) var(--border-radius);
  }
  
  @media (max-width: 768px) {
    padding: 20px 15px;
    margin-bottom: 25px;
  }
`;

const Title = styled.h1`
  color: var(--primary-color);
  margin-bottom: 15px;
  font-size: 2.5rem;
  font-weight: 700;
  animation: fadeIn 0.7s ease-in-out;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.2rem;
  margin: 10px 0 0;
  animation: fadeIn 0.7s 0.2s both ease-in-out;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const EventInfoBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  color: var(--text-light);
  font-size: 0.95rem;
  animation: fadeIn 0.7s 0.3s both ease-in-out;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    color: var(--primary-color);
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 50px 30px;
  color: var(--text-primary);
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  animation: slideUp 0.5s ease-out;
  margin: auto 0;
  max-width: 600px;
  margin: 10px auto;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  h2 {
    color: var(--error-color);
    margin-bottom: 15px;
    font-size: 1.8rem;
  }
  
  p {
    font-size: 1.1rem;
    color: var(--text-secondary);
    line-height: 1.6;
  }
  
  svg {
    color: var(--error-color);
    font-size: 3rem;
    margin-bottom: 20px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  font-size: 1.2rem;
  color: var(--primary-color);
  min-height: 50vh;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 80px;
  height: 80px;
  margin-bottom: 20px;

  &:after {
    content: " ";
    display: block;
    width: 64px;
    height: 64px;
    margin: 8px;
    border-radius: 50%;
    border: 6px solid var(--primary-color);
    border-color: var(--primary-color) transparent var(--primary-color) transparent;
    animation: spin 1.2s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-weight: 500;
  margin-top: 20px;
  animation: pulse 1.5s infinite ease-in-out;
  
  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 1; }
    100% { opacity: 0.6; }
  }
`;

const SlideshowWrapper = styled.div`
  margin-top: 20px;
  animation: fadeIn 0.8s ease-in-out;
  position: relative;
`;

const Footer = styled.footer`
  @font-face {
    font-family: 'NouvelR';
    src: url('/fonts/NouvelR-R5-Extrabold.otf') format('opentype');
    font-weight: bold;
    font-style: normal;
  }
  
  font-family: 'NouvelR';
  text-align: center;
  position:fixed;
  bottom:0;
  left:0;
  right:0;
  font-size: 1.1rem;
  color: var(--paper-color);
  animation: fadeIn 0.8s ease-in-out;
  `;

const CopyButton = styled.button`
  margin-top: 5px;
  margin-bottom: 10px;
  padding: 6px 12px;
  font-size: 0.9rem;
  background-color: rgb(255, 146, 44);
  color: white;
  border-radius: 10px;
  cursor: pointer;
  transition: background-color 0.5s ease;

  &:hover {
    background-color: rgb(255, 199, 44);
  }
`;

const Logo = styled.div`
  text-align: center;
  margin-top: 10px;
  font-weight: bold;
  color: var(--primary-color);

  img {
    width: 128px;
    hright: 182px;
  }

`;

const LogoAccent = styled.span`
  color: var(--secondary-color);
`;

const InfoCard = styled.div`
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  padding: 25px;
  margin: 0 auto 30px;
  max-width: 800px;
  box-shadow: var(--box-shadow);
  animation: slideUp 0.5s ease-out;
  text-align: center;
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
    margin: 0;
  }
  
  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

// Icon components
const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3.01 3.9 3.01 5L3 19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19ZM7 10H12V15H7V10Z" fill="currentColor"/>
  </svg>
);

const ImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.5L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
  </svg>
);

const ErrorIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z" fill="currentColor"/>
  </svg>
);

const EmptyGalleryIcon = () => (
  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 5H19C20.1 5 21 5.9 21 7V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V5ZM19 19V7H5V19H19ZM13.96 12.29L11.21 15.83L9.25 13.47L6.5 17H17.5L13.96 12.29Z" fill="currentColor" fillOpacity="0.5"/>
    <path d="M5 19H19V7H5V19ZM13.96 12.29L11.21 15.83L9.25 13.47L6.5 17H17.5L13.96 12.29Z" fill="currentColor" fillOpacity="0.2"/>
  </svg>
);

const hashtags = "#r5isback | #bestenbaslayalim | @renaultturkiye";

const VisitorGallery: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [images, setImages] = useState<Image[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [visitorName, setVisitorName] = useState<string>('');
  const [eventDetails, setEventDetails] = useState({
    date: 'Recent Event',
    imageCount: 0
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchVisitorImages = async () => {
      try {
        setLoading(true);
        
        if (!token) {
          setError('Invalid gallery access. Please scan a valid QR code.');
          setLoading(false);
          return;
        }

        console.log('Fetching images for token:', token);
        const response = await publicApi.get(`/api/qrcodes/gallery/${token}`);
        console.log('Gallery response:', response.data);
        
        setImages(response.data.images);
        setVisitorName(response.data.visitorName || 'Guest');
        // Set event details - in a real app, this would come from the API
        setEventDetails({
          date: new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          imageCount: response.data.images.length
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching visitor images:', err);
        setError('Unable to load your images. Please try again later or contact the event organizer.');
        setLoading(false);
      }
    };

    fetchVisitorImages();
  }, [token]);

  if (loading) {
    return (
      <GalleryContainer>
        <ContentWrapper>
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading your photos...</LoadingText>
          </LoadingContainer>
        </ContentWrapper>
      </GalleryContainer>
    );
  }

  if (error) {
    return (
      <GalleryContainer>
        <ContentWrapper>
          <ErrorMessage>
            <ErrorIcon />
            <h2>Oops! Something went wrong</h2>
            <p>{error}</p>
          </ErrorMessage>
        </ContentWrapper>
      </GalleryContainer>
    );
  }

  return (
    <GalleryContainer>
      <ContentWrapper>
        {/* <Header>
          <Title>Your Event Photos</Title>
          <Subtitle>Hello, {visitorName}! Here are your photos from the event.</Subtitle>
          
          <EventInfoBar>
            <InfoItem>
              <CalendarIcon />
              <span>{eventDetails.date}</span>
            </InfoItem>
            <InfoItem>
              <ImageIcon />
              <span>{eventDetails.imageCount} Photos</span>
            </InfoItem>
          </EventInfoBar>
        </Header> */}

        {images.length === 0 ? (
          <ErrorMessage>
            <EmptyGalleryIcon />
            <h2>No Images Found</h2>
            <p>There are no images associated with this QR code yet. Please check back later.</p>
          </ErrorMessage>
        ) : (
          <>
            {/* <InfoCard>
              <p>Browse through all photos from the event. Click on the thumbnails below to view specific images or use the navigation arrows to move between photos.</p>
            </InfoCard> */}
            
            <SlideshowWrapper>
              <Slideshow images={images} />
            </SlideshowWrapper>
          </>
        )}

        <Footer>
          <Logo>
            <img src= {process.env.PUBLIC_URL + "/R5logo.png"} alt=""/>
          </Logo>
          <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#000' }}>
            <div>@renaultturkiye</div>
            <div style={{ marginTop: '6px' }}>
              #R5ISBACK&nbsp;&nbsp;#BEŞTENBAŞLAYALIM
            </div>
          </div>

          <CopyButton
            onClick={() => {
              navigator.clipboard.writeText('#R5ISBACK #BEŞTENBAŞLAYALIM @renaultturkiye');
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? 'Kopyalandı!' : 'Hashtagleri Kopyala'}
          </CopyButton>
        </Footer>
      </ContentWrapper>
    </GalleryContainer>
  );
};

export default VisitorGallery;